import React from 'react';
import styled from 'styled-components';
import { TranscriptElement } from '../../../types.ts/transcript';
import { useEditor } from '../../../stores-v2/StoreContext';
import { TranscriptSimpleRemoveCommand } from '../../commands/TranscriptSimpleRemoveCommand';

interface TranscriptContextMenuProps {
  x: number;
  y: number;
  selectedWords: TranscriptElement[];
  isActive: boolean; // Whether viewing active transcript (vs full)
  canRemove?: boolean; // Optional flag to explicitly control if remove is available
  canRestore?: boolean; // Optional flag to explicitly control if restore is available
  elementId?: string; // ID of the video element containing the words (for SimpleRemove)
  prevElementId?: string | null; // ID of the previous element (for SimpleRestore)
  nextElementId?: string | null; // ID of the next element (for SimpleRestore)
  onRemove: () => void;
  onRestore: () => void; // Added onRestore callback
  onReplace: () => void;
  onAttachPhoto?: () => void; // Option for attaching photos
  onCut?: () => void; // Option for cutting words
  onPaste?: () => void; // Option for pasting words
  hasCutWords?: boolean; // Whether there are cut words available to paste
  onClose: () => void;
}

/**
 * Context menu for transcript elements
 */
const TranscriptContextMenu: React.FC<TranscriptContextMenuProps> = ({
  x,
  y,
  selectedWords,
  isActive,
  canRemove,
  canRestore,
  elementId,
  prevElementId,
  nextElementId,
  onRemove,
  onRestore,
  onReplace,
  onAttachPhoto,
  onCut,
  onPaste,
  hasCutWords,
  onClose,
}) => {
  // Get editor from context
  const editorStore = useEditor();

  // In the transcript, there are different rules for each view mode:
  // - Active view: all visible words can be removed
  // - Original view:
  //   - Active words (shown in white) can be removed
  //   - Inactive words (shown in gray) can be restored

  // If canRemove/canRestore are explicitly passed, use them directly
  // These values should be determined by useTranscriptStatus helper
  const allCanBeRemoved = canRemove !== undefined ? canRemove : false;
  const allCanBeRestored = canRestore !== undefined ? canRestore : false;
  const canBeReplaced =
    selectedWords.length === 1 && selectedWords[0].type === 'text';
  const canBeCut = onCut && selectedWords.length > 0 && allCanBeRemoved;
  const canPaste = onPaste && hasCutWords;

  // Handle clicks outside of menu to close it
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.transcript-context-menu')) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Helper to handle remove with SimpleRemoveCommand
  const handleSimpleRemove = () => {
    // Check if we can use the new command approach
    if (editorStore?.editor && selectedWords.length > 0 && elementId) {
      try {
        // Get the initial_index values from selected words
        const initialIndices = selectedWords
          .filter(
            (word) => word.type === 'text' && word.initial_index !== undefined,
          )
          .map((word) => word.initial_index);

        if (initialIndices.length === 0) {
          console.warn('No valid initial_index values found in selected words');
          onRemove();
          onClose();
          return;
        }

        console.log('Removing words with initial indices:', initialIndices);
        console.log('From element ID:', elementId);

        // Find min and max to get range
        const startInitialIdx = Math.min(...initialIndices);
        const endInitialIdx = Math.max(...initialIndices);

        console.log(
          `Using index range for remove: ${startInitialIdx}-${endInitialIdx}`,
        );

        // Log information about the containing element
        const element = editorStore.editor.getElementById(elementId);
        if (element) {
          console.log('Containing element:', {
            id: element.id,
            time: element.time,
            end_time: element.end_time,
            trim_start: (element as any).trim_start,
          });
        } else {
          console.warn(`Element with ID ${elementId} not found`);
        }

        // Create and execute the command
        const removeCommand = new TranscriptSimpleRemoveCommand(
          editorStore.editor,
          elementId,
          startInitialIdx,
          endInitialIdx,
          false,
          false,
        );

        const result = editorStore.editor.executeCommand(removeCommand);
        console.log('SimpleRemoveCommand execution result:', result);
      } catch (err) {
        console.error('Error executing SimpleRemoveCommand:', err);
      }
    } else {
      console.log('SimpleRemove skipped - conditions not met:', {
        editorAvailable: !!editorStore?.editor,
        wordsSelected: selectedWords.length,
        elementIdProvided: !!elementId,
      });
    }

    // Still call original onRemove for any cleanup/UI updates
    onRemove();
    onClose();
  };

  // Simply call the onRestore handler passed from parent component
  const handleRestore = () => {
    onRestore();
    onClose();
  };

  return (
    <MenuContainer
      className="transcript-context-menu"
      style={{ left: x, top: y }}
    >
      <h3>
        Selected: {selectedWords.length} word
        {selectedWords.length !== 1 ? 's' : ''}
      </h3>

      {/* Context menu options */}
      <MenuOptions>
        {canBeReplaced && (
          <MenuItem onClick={onReplace} data-testid="replace-word-option">
            Replace word
          </MenuItem>
        )}

        {allCanBeRemoved && (
          <MenuItem
            onClick={handleSimpleRemove}
            data-testid="remove-word-option"
          >
            Remove from video
          </MenuItem>
        )}

        {allCanBeRestored && (
          <MenuItem onClick={handleRestore} data-testid="restore-word-option">
            Restore to video
          </MenuItem>
        )}

        {canBeCut && (
          <MenuItem onClick={onCut} data-testid="cut-word-option">
            Cut
          </MenuItem>
        )}

        {canPaste && (
          <MenuItem onClick={onPaste} data-testid="paste-word-option">
            Paste
          </MenuItem>
        )}

        {onAttachPhoto && selectedWords.length > 0 && (
          <MenuItem onClick={onAttachPhoto} data-testid="attach-photo-option">
            Attach Photo
          </MenuItem>
        )}

        {!allCanBeRemoved &&
          !allCanBeRestored &&
          !canBeReplaced &&
          !onAttachPhoto &&
          !canBeCut &&
          !canPaste && (
            <MenuInfo>
              Select only words that can all be removed or restored
            </MenuInfo>
          )}
      </MenuOptions>
    </MenuContainer>
  );
};

const MenuContainer = styled.div`
  position: fixed; /* Use fixed position to handle scrolling correctly */
  background-color: #252533;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 8px;
  min-width: 200px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  transform-origin: top left; /* Ensure the menu grows in the right direction */

  h3 {
    margin: 0 0 8px 0;
    font-size: 14px;
    color: #eee;
    border-bottom: 1px solid #444;
    padding-bottom: 6px;
  }
`;

const MenuOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MenuItem = styled.button`
  background-color: transparent;
  border: none;
  color: white;
  padding: 6px 8px;
  text-align: left;
  cursor: pointer;
  border-radius: 3px;

  &:hover {
    background-color: #1e3a8a;
  }

  &:active {
    background-color: #0c2d6b;
  }
`;

const MenuInfo = styled.div`
  color: #999;
  font-size: 12px;
  padding: 8px;
  text-align: center;
  font-style: italic;
`;

export default TranscriptContextMenu;
